/**
 * Native `<select>` primitive, styled to match `TextInput`. Controlled
 * (`value` + `onValue`), token-driven, optional visible label, invalid state
 * (`aria-invalid`). Native element = free keyboard support (Up/Down, type-
 * ahead) and screen-reader semantics — no custom listbox to reimplement.
 */
import type { JSX } from 'solid-js';
import { createEffect, createUniqueId, For, Show, splitProps } from 'solid-js';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

const STYLE_ID = 'f4-select';
const CSS = `
.f4-select-field { display: flex; flex-direction: column; gap: var(--space-1); }
.f4-select-field__label { font-size: var(--font-size-sm); font-weight: var(--font-weight-semibold); color: var(--color-text); }
.f4-select {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  font: inherit;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: border-color var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-select { --f4-focus-offset: 1px; }
.f4-select:focus-visible { border-color: var(--color-accent); }
.f4-select[aria-invalid="true"] { border-color: var(--color-danger); }
.f4-select[disabled] { opacity: 0.6; cursor: not-allowed; background: var(--color-surface-sunken); }
@media (prefers-reduced-motion: reduce) { .f4-select { transition: none; } }
`;

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps
  extends Omit<JSX.SelectHTMLAttributes<HTMLSelectElement>, 'onInput' | 'value'> {
  /** Options rendered in order. */
  readonly options: readonly SelectOption[];
  /** Current value. */
  readonly value: string;
  /** Emitted with the new value on change. */
  readonly onValue: (value: string) => void;
  /** Visible label text. Rendered as a `<label>` when provided. */
  readonly label?: string;
  /** Element id — links the label. Auto-generated when a `label` is set but no id is passed. */
  readonly id?: string;
  /** Mark the field invalid (`aria-invalid`). */
  readonly invalid?: boolean;
  readonly class?: string;
}

export function Select(props: SelectProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);
  const [local, rest] = splitProps(props, [
    'options',
    'value',
    'onValue',
    'label',
    'id',
    'invalid',
    'class',
  ]);
  // A visible label must point at a real control. When the caller supplies a
  // label but no id, fall back to a stable generated id so the association is
  // never dropped (an unassociated <label> leaves screen readers without the
  // advertised accessible name).
  const generatedId = createUniqueId();
  const fieldId = (): string | undefined =>
    local.id ?? (local.label !== undefined ? generatedId : undefined);

  // Controlled <select>: assign `.value` in an effect that runs AFTER `<For>` has
  // inserted the options. A bare `value` prop is applied before the options exist,
  // so it finds no match and the control falls back to the first option — a
  // persisted non-first timezone/locale re-hydrated on boot would render as "auto".
  // Setting `.value` post-mount selects the right option, and the effect re-runs on
  // every controlled-value change, so the field stays controlled either way.
  let selectRef: HTMLSelectElement | undefined;
  createEffect(() => {
    const el = selectRef;
    if (el && el.value !== local.value) {
      el.value = local.value;
    }
  });

  return (
    <div class="f4-select-field">
      <Show when={local.label}>
        <label class="f4-select-field__label" for={fieldId()}>
          {local.label}
        </label>
      </Show>
      <select
        {...rest}
        ref={selectRef}
        id={fieldId()}
        class={cx('f4-select', 'f4-focus-ring', local.class)}
        aria-invalid={local.invalid ? 'true' : undefined}
        onInput={(event) => local.onValue(event.currentTarget.value)}
      >
        <For each={local.options}>
          {(option) => (
            <option value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          )}
        </For>
      </select>
    </div>
  );
}
